import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import type { PayloadAction } from '@reduxjs/toolkit'
import { CompaniesService } from '@/lib/api/services/companies'
import { RootState } from '../index'

interface Company {
  id?: string | number
  openUrl?: string
  name?: string
  imageUrl?: string
  coverImageUrl?: string
  employeeSize?: string
  kabu?: string
  categories?: string[]
  regionName?: string
  enName?: string
  summary?: string
  tabs: string[]
  facebook?: string
  twitter?: string
  youtube?: string
  instagram?: string
  wantedly?: string
  linkedin?: string
  tiktok?: string
  line?: string
  actionBtns?: Array<{ label: string; url: string }>
}

interface CompaniesState {
  companyCommonInfo: Company | null
  isLoading: boolean
  error: string | null
}

const initialState: CompaniesState = {
  companyCommonInfo: null,
  isLoading: true,
  error: null,
}

// 企業共通情報を取得するAsync thunk
export const fetchCompanyCommonInfo = createAsyncThunk(
  'companies/fetchCompanyCommonInfo',
  async (
    { companyId, isPreview = false }: { companyId: string; isPreview?: boolean },
    { getState, rejectWithValue },
  ) => {
    const state = getState() as RootState
    const currentCompanyInfo = state.companies.companyCommonInfo

    // 同一企業の場合はスキップ
    if (currentCompanyInfo && currentCompanyInfo.openUrl === companyId) {
      return currentCompanyInfo
    }

    const data = await CompaniesService.getCompanyInfo(companyId, isPreview, {
      skipRequestCancellation: true,
    })

    return {
      coverImageUrl: data.coverImageUrl || '',
      tabs: data.tabs || ['books', 'contents', 'movies', 'company-info'],
      ...data,
    }
  },
)

export const companiesSlice = createSlice({
  name: 'companies',
  initialState,
  reducers: {
    clearCompanyCommonInfo: (state) => {
      state.companyCommonInfo = null
      state.isLoading = true
      state.error = null
    },
    setCompanyCommonInfo: (state, action: PayloadAction<Company>) => {
      state.companyCommonInfo = action.payload
      state.isLoading = false
      state.error = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCompanyCommonInfo.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(fetchCompanyCommonInfo.fulfilled, (state, action) => {
        state.companyCommonInfo = action.payload
        state.isLoading = false
        state.error = null
      })
      .addCase(fetchCompanyCommonInfo.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.error.message || '企業情報の取得に失敗しました'
        // エラー時はデフォルトデータを設定
        state.companyCommonInfo = {
          coverImageUrl: '',
          tabs: ['books', 'contents', 'movies', 'company-info'],
        }
      })
  },
})

export const { clearCompanyCommonInfo, setCompanyCommonInfo } = companiesSlice.actions

export const selectCompanyCommonInfo = (state: RootState) => state.companies.companyCommonInfo
export const selectCompanyOpenUrl = (state: RootState) =>
  state.companies.companyCommonInfo?.openUrl || ''
export const selectCompaniesLoading = (state: RootState) => state.companies.isLoading
export const selectCompaniesError = (state: RootState) => state.companies.error

export default companiesSlice.reducer
